import type { BranchConfigRecord } from "@/api/useInventory.types"

const INVENTORY_RESOURCE = "/v1/inventory"
const INTEGRATIONS = "/v1/integrations"
const SBOM = "/v1/sbom"
const SETTINGS = "/v1/settings"
const TICKETING = "/v1/ticketing"
const ISSUES = "/v1/issues"
const TRIAGE = "/v1/triage"
const AUTH = "/v1/auth"
const TENANTS = "/v1/tenants"
const ONBOARDING = "/v1/onboarding"
const LABELS = "/v1/labels"
const EXPORT = "/export"
const WORKFLOWS = `${SETTINGS}/workflows`
const INCIDENTS = "/v1/incidents"
const REPORTS = "/v1/reports"
const REPORTS_GENERATE = `/v1/reports-generate`
const SCAN_HISTORY = "/v1/scan-history"
const SCM_SIGN_IN = "/v1/scm"
const POLICY_EXCEPTIONS = "/v1/policy-exceptions"
const ASSETS_DB = "/v1/assets-db"
const EXPERIMENTAL = "/v1/experimental"
const EVP = `${EXPERIMENTAL}/evp`
const QUICK_WINS = `${TRIAGE}/quick-wins`
const SAST = `${SETTINGS}/sast`
const SAST_GUIDANCE = `${SAST}/guidance`
const AGENT_PLATFORMS = "/v1/deployments/agent-platforms"

export const RESOURCES_NAMES = {
  AUTHENTICATION: {
    AUTH: AUTH,
    TENANT: `${AUTH}/tenant`,
    API_KEYS: `${AUTH}/api-keys`,
    DEPLOYMENT_KEYS: `${AUTH}/deployment-keys`,
    SERVICE_KEYS: `${AUTH}/service-keys`,
    SCIM_KEY: `${AUTH}/scim-key`,
    ACCEPT_INVITE: `${AUTH}/accept-invite`,
    FORGOT_PASSWORD: `${AUTH}/forgot-password`,
    LOGIN: `${AUTH}/login`,
    LOGOUT: `${AUTH}/logout`,
    RESET_PASSWORD: `${AUTH}/reset-password`,
    DOCS: `${AUTH}/docs`,
    CLI_CALLBACK: `${AUTH}/cli/callback`,
    MCP: `${AUTH}/mcp`,
    MCP_REQUEST: `${AUTH}/mcp/request`,
    MCP_APPROVE: `${AUTH}/mcp/approve`,
    MCP_DENY: `${AUTH}/mcp/deny`,
    SSO: `${AUTH}/sso`
  },
  POSTURE: "/v1/posture",
  INVENTORY: INVENTORY_RESOURCE,
  TENANTS: {
    GCP_TOKEN_VALIDATION: `${TENANTS}/gcp-marketplace/validate`,
    SIGNUP: `${TENANTS}`,
    CONFIGURATIONS: `${TENANTS}/configurations`,
    AI_PROVIDER: `${TENANTS}/configurations/ai-provider`,
    AI_PROVIDER_TEST: `${TENANTS}/configurations/ai-provider/test`
  },
  SETTINGS: {
    USERS: `${SETTINGS}/users`,
    USERS_EXPORT: `${SETTINGS}/users/export`,
    USERS_FILTERS: `${SETTINGS}/users/filters`,
    SCOPES: `${SETTINGS}/scopes`,
    AUTH_METHODS: `${SETTINGS}/auth_methods`,
    POLICIES: {
      LIST: `${SETTINGS}/policies`,
      CONDITIONS: { OPTIONS: `${SETTINGS}/policies/conditions/options` },
      SCOPE: {
        OPTIONS_SCM: `${SETTINGS}/policies/scope/options`,
        OPTIONS_CI: `${SETTINGS}/ci-policies/scope/options`
      }
    },
    CI_POLICIES: {
      LIST: `${SETTINGS}/ci-policies`,
      CONDITIONS: { OPTIONS: `${SETTINGS}/ci-policies/conditions/options` }
    },
    SAST_POLICIES: {
      // Todo: direct to correct api
      LIST: `${SETTINGS}/ci-policies`,
      CONDITIONS: { OPTIONS: `${SETTINGS}/ci-policies/conditions/options` }
    },
    SERVICE_KEYS: `${SETTINGS}/service-keys`,
    SAST: {
      ROOT: SAST,
      GUIDANCE: {
        ROOT: SAST_GUIDANCE,
        OPTIONS: `${SAST_GUIDANCE}/options`,
        SCOPE_OPTIONS: `${SAST_GUIDANCE}/scope/options`,
        VALIDATE: `${SAST_GUIDANCE}/validate`,
        PREVIEW: `${SAST_GUIDANCE}/preview`
      }
    },
    WORKFLOWS: {
      LIST: WORKFLOWS,
      ACTIONS: `${WORKFLOWS}/actions`,
      TRIGGER: `${WORKFLOWS}/trigger`,
      STATISTICS: `${WORKFLOWS}/statistics`
    }
  },
  INVENTORIES: {
    DIRECT_DEPENDENCY: `${INVENTORY_RESOURCE}/direct_dependency`,
    CONTAINER_BASE_IMAGE: `${INVENTORY_RESOURCE}/container_base_image`,
    CONTAINER_BASE_IMAGE_EXPORT: `${INVENTORY_RESOURCE}/container_base_image${EXPORT}`,
    BRANCH: `${INVENTORY_RESOURCE}/branch`,
    BRANCH_EXPORT: `${INVENTORY_RESOURCE}/branch${EXPORT}`,
    // The branch record, when known, addresses the config endpoints by the stable code-repo id so a
    // rename can't misroute; the id is read from the record here rather than threaded by callers.
    BRANCH_MODULES: (repoName: string, record?: BranchConfigRecord) =>
      `${INVENTORY_RESOURCE}/branch/${repoName}/modules` +
      (record?.codeRepoId
        ? `?repo_id=${encodeURIComponent(record.codeRepoId)}`
        : ""),
    BRANCH_CONFIG: (repoName: string, record?: BranchConfigRecord) =>
      `${INVENTORY_RESOURCE}/branch/${repoName}/config` +
      (record?.codeRepoId
        ? `?repo_id=${encodeURIComponent(record.codeRepoId)}`
        : ""),
    BRANCH_AGENTS: (repoName: string) =>
      `${INVENTORY_RESOURCE}/branch/${repoName}/agents`,
    ARCHIVED: `${INVENTORY_RESOURCE}/branch/archived`,
    IMAGES: `${INVENTORY_RESOURCE}/container_image`,
    IMAGES_EXPORT: `${INVENTORY_RESOURCE}/container_image${EXPORT}`,
    COUNTS: `${INVENTORY_RESOURCE}/counts`,
    APPLICATION: `${INVENTORY_RESOURCE}/application`,
    APPLICATION_EXPORT: `${INVENTORY_RESOURCE}/application${EXPORT}`,
    APPLICATION_TOOL_INVOCATIONS: (applicationId: string) =>
      `${INVENTORY_RESOURCE}/application/${applicationId}/tool-invocations`,
    ENVIRONMENT: `${INVENTORY_RESOURCE}/environment`,
    SNAPSHOT: `${INVENTORY_RESOURCE}/snapshot`,
    RUNTIME_COVERAGE: `${INVENTORY_RESOURCE}/runtime_coverage`,
    SAST_ISSUES: `${INVENTORY_RESOURCE}/branch/sast_issues`,
    SAST_ISSUES_COUNT: `${INVENTORY_RESOURCE}/branch/sast_issues/count`,
    PACKAGE: `${INVENTORY_RESOURCE}/packages`,
    PACKAGE_EXPORT: `${INVENTORY_RESOURCE}/packages${EXPORT}`,
    PACKAGE_REFERENCES_COUNT: `${INVENTORY_RESOURCE}/packages/projects/count`,
    PACKAGE_FILTERS: `${INVENTORY_RESOURCE}/packages/filters`,
    VM: `${INVENTORY_RESOURCE}/vm`,
    VM_EXPORT: `${INVENTORY_RESOURCE}/vm${EXPORT}`,
    THREAT_SPOTLIGHT: `/v1/threat-spotlight`,
    AI_DISCOVERY: `${INVENTORY_RESOURCE}/ai`
  },
  SBOM: { EXPORT: `${SBOM}${EXPORT}` },
  INTEGRATIONS: {
    LIST: INTEGRATIONS,
    CALLBACK: `${INTEGRATIONS}/callback`,
    NOTIFY_CALLBACK: `${INTEGRATIONS}/callback/notify-admin`,
    ECR: { PRE_INSTALL: `${INTEGRATIONS}/pre-install/ecr` },
    GITHUB: { PRE_INSTALL: `${INTEGRATIONS}/pre-install/github` },
    GCR: { PRE_INSTALL: `${INTEGRATIONS}/pre-install/gcr` },
    ACR: { PRE_INSTALL: `${INTEGRATIONS}/pre-install/acr` },
    ASANA: { PRE_INSTALL: `${INTEGRATIONS}/pre-install/asana` },
    DOWNLOAD: `${INTEGRATIONS}/download`
  },
  ALERTS: "/v1/alerts",
  REMEDIES: "/v1/remedies",
  DEPLOYMENTS: "/v1/deployments",
  AGENT_PLATFORMS: {
    PLATFORM: (platform: string) => `${AGENT_PLATFORMS}/${platform}`,
    PRE_INSTALL: (platform: string) =>
      `${AGENT_PLATFORMS}/${platform}/pre-install`,
    // The platform's connections, each an AWS account or an Entra tenant
    CONNECTIONS: (platform: string) =>
      `${AGENT_PLATFORMS}/${platform}/connections`
  },
  AI_POLICIES: {
    LIST: `${SETTINGS}/ai-policies`,
    SCOPE_OPTIONS: `${SETTINGS}/ai-policies/scope/options`,
    MCP_SERVER_SCOPE_OPTIONS: `${SETTINGS}/ai-policies/scope/options/mcp-servers`,
    CONDITIONS_OPTIONS: `${SETTINGS}/ai-policies/conditions/options`,
    DECISIONS: "/v1/ai/policy-decisions"
  },
  TICKETING: {
    JIRA: `${TICKETING}/jira`,
    METADATA: (vendor: string) => `${TICKETING}/${vendor}/metadata`,
    FIELDS: (vendor: string) => `${TICKETING}/${vendor}/fields`
  },
  ISSUES: {
    REPOSITORIES: `${ISSUES}/repositories`,
    IMAGES: `${ISSUES}/images`
  },
  TRIAGE: {
    PROJECTS: `${TRIAGE}/projects`,
    FILTERS: `${TRIAGE}/filters`,
    ISSUES: `${TRIAGE}/issues`,
    ISSUES_COUNT: `${TRIAGE}/issues/count`,
    ISSUES_GENERATE: "fix",
    ISSUES_APPLY: "apply",
    EXPORT: `${TRIAGE}/issues${EXPORT}`,
    CHAINS: `${TRIAGE}/chains`,
    QUICK_WINS: {
      ROOT: QUICK_WINS,
      SUMMARY: `${QUICK_WINS}/summary`,
      ISSUES: (id: string) => `${QUICK_WINS}/${id}/issues`,
      IGNORE: (id: string) => `${QUICK_WINS}/${id}/ignore`
    }
  },
  ONBOARDING: {
    ONBOARDING_STATUS: `${ONBOARDING}/status`,
    DISMISS_ONBOARDING: `${ONBOARDING}/dismiss-onboarding`,
    WELCOME_STATUS: `${ONBOARDING}/welcome-status`,
    DISMISS_WELCOME: `${ONBOARDING}/dismiss-welcome`
  },
  LABELS: { LIST: `${LABELS}`, COLORS: `${LABELS}/colors` },
  STATISTICS: "/v1/statistics",
  INCIDENTS: {
    LIST: `${INCIDENTS}`,
    DESCRIBE: `${INCIDENTS}/describe`,
    HISTORY: `${INCIDENTS}/history`,
    DISMISS: `${INCIDENTS}/dismiss`,
    PHASE: `${INCIDENTS}/phase`,
    RULES: `${INCIDENTS}/rules`,
    ENVIRONMENTS: `${INCIDENTS}/environments`,
    ADR_STATE: `${INCIDENTS}/adr/state`
  },
  REPORTS: {
    DEFINITIONS: `${REPORTS}/definitions`,
    JOBS: `${REPORTS}/jobs`,
    GENERATE: REPORTS_GENERATE
  },
  KAI: {
    STREAM: "/v1/kai/stream"
  },
  SCAN_HISTORY: {
    ROOT: SCAN_HISTORY,
    FILTERS: `${SCAN_HISTORY}/filters`,
    STATISTICS: `${SCAN_HISTORY}/statistics`,
    EXPORT: `${SCAN_HISTORY}${EXPORT}`
  },
  POLICY_EXCEPTIONS: {
    SCAN: (scanId: string) =>
      `${POLICY_EXCEPTIONS}/scans/${encodeURIComponent(scanId)}`,
    REQUESTS: `${POLICY_EXCEPTIONS}/requests`
  },
  // Reached without a Kodem session.
  SCM_SIGN_IN: {
    ROOT: `${SCM_SIGN_IN}/`,
    AUTHORIZE_URL: `${SCM_SIGN_IN}/auth/authorize-url`,
    VERIFY: `${SCM_SIGN_IN}/auth/verify`,
    RETURN_URL: `${SCM_SIGN_IN}/auth/return-url`,
    POLICY_EXCEPTION_SCAN: (scanId: string) =>
      `${SCM_SIGN_IN}/policy-exception/scans/${encodeURIComponent(scanId)}`
  },
  ASSETS_DB: {
    VERSION: `${ASSETS_DB}/version`
  },
  EXPERIMENTAL: {
    AI_DISCOVERY: `${EXPERIMENTAL}/ai-discovery`,
    EVP: {
      ISSUES: `${EVP}/issues`,
      MITIGATION_VALIDATIONS: `${EVP}/mitigations/validations`
    }
  }
}
